/**
 * Sprint 006 (A3) — Shared debounced search input.
 *
 * Generalizes InvoiceSearchBar (T114) so every list page gets the same
 * search anatomy: icon + debounced controlled input + submit-on-enter.
 * External `value` stays the source of truth (URL state); local state
 * only buffers keystrokes until the debounce fires.
 */
import { useState, useEffect, type FormEvent } from 'react';
import { Input } from './Input';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  'data-testid'?: string;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Suchen…',
  debounceMs = 300,
  ...rest
}: SearchInputProps) {
  const [localValue, setLocalValue] = useState(value);

  // Sync external value changes (URL navigation, filter reset)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [localValue, debounceMs, onChange, value]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onChange(localValue);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full" role="search">
      <Input
        placeholder={placeholder}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        }
        {...rest}
      />
    </form>
  );
}

export default SearchInput;
